import { Component } from '@theme/component';

/**
 * A custom element that handles email signup form submission with Klaviyo integration.
 *
 * @extends {Component}
 */
class EmailSignupFormComponent extends Component {
  /** @type {boolean} */
  isSubmitting = false;

  /** @type {string | undefined} */
  klaviyoListId;

  /** @type {string | undefined} */
  klaviyoCompanyId;

  /** @type {string | undefined} */
  blockId;

  /** @type {HTMLFormElement | null} */
  form = null;

  /** @type {HTMLInputElement | null} */
  emailInput = null;

  /** @type {HTMLButtonElement | null} */
  submitButton = null;

  connectedCallback() {
    super.connectedCallback();

    this.form = this.querySelector('form');
    if (!this.form) return;

    this.emailInput = this.form.querySelector('input[type="email"]');
    this.submitButton = this.form.querySelector('button[type="submit"]');

    this.klaviyoListId = this.form.dataset.klaviyoListId;
    this.klaviyoCompanyId = this.form.dataset.klaviyoCompanyId;
    this.blockId = this.form.dataset.blockId;

    if (this.klaviyoCompanyId) {
      this.form.setAttribute('data-klaviyo-enabled', 'true');
    }

    this.form.addEventListener('submit', this.#handleSubmit.bind(this), true);

    requestAnimationFrame(() => {
      if (this.klaviyoCompanyId) {
        this.#hideMessages();
      }
    });
  }

  /**
   * Handles form submission.
   * @param {Event} event - The submit event.
   */
  async #handleSubmit(event) {
    if (this.isSubmitting) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (!this.klaviyoCompanyId) {
      return;
    }

    if (!this.emailInput) {
      console.error('Email input element not found');
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const email = this.emailInput.value?.trim();

    if (!this.emailInput.validity.valid) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (!email || !this.#isValidEmail(email)) {
      this.#showValidationError();
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    this.isSubmitting = true;
    this.#setButtonLoading(true);
    this.#hideMessages();

    try {
      const success = await this.#submitToKlaviyo(email);
      if (success) {
        this.#showSuccess();
        this.form?.reset();
      } else {
        this.#showError();
        return;
      }
    } catch (error) {
      console.error('Klaviyo submission error:', error);
      this.#showError();
      return;
    } finally {
      this.isSubmitting = false;
      this.#setButtonLoading(false);
    }
  }

  /**
   * Submits email to Klaviyo API.
   * @param {string} email - The email address.
   * @returns {Promise<boolean>} Whether the submission was successful.
   */
  async #submitToKlaviyo(email) {
    if (!this.klaviyoCompanyId) {
      console.error('Klaviyo Company ID (Public API Key) is required');
      return false;
    }

    /** @type {any} */
    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                subscriptions: {
                  email: {
                    marketing: {
                      consent: 'SUBSCRIBED',
                    },
                  },
                },
              },
            },
          },
        },
      },
    };

    if (this.klaviyoListId) {
      payload.data.relationships = {
        list: {
          data: {
            type: 'list',
            id: this.klaviyoListId,
          },
        },
      };
    }

    const apiUrl = `https://a.klaviyo.com/client/subscriptions?company_id=${this.klaviyoCompanyId}`;

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          accept: 'application/vnd.api+json',
          revision: '2026-01-15',
          'content-type': 'application/vnd.api+json',
        },
        body: JSON.stringify(payload),
      });

      return response.ok;
    } catch (error) {
      console.error('Klaviyo API error:', error);
      return false;
    }
  }

  /**
   * Validates email format.
   * @param {string} email - The email address to validate.
   * @returns {boolean} Whether the email is valid.
   */
  #isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  /**
   * Shows success message.
   */
  #showSuccess() {
    const successMessage = /** @type {HTMLElement | null} */ (
      this.form?.querySelector(`#Email-signup__message-klaviyo-success-${this.blockId}`)
    );
    const klaviyoErrorMessage = /** @type {HTMLElement | null} */ (
      this.form?.querySelector(`#Email-signup__message-klaviyo-error-${this.blockId}`)
    );
    const shopifySuccessMessage = this.form?.querySelector(
      `#Email-signup__message-success-${this.blockId}`
    );

    if (klaviyoErrorMessage) {
      klaviyoErrorMessage.hidden = true;
    }

    if (successMessage) {
      successMessage.hidden = false;
      successMessage.focus();
      this.emailInput?.setAttribute('aria-describedby', successMessage.id);
    }

    if (shopifySuccessMessage) {
      shopifySuccessMessage.classList.add('email-signup__message--hidden');
    }
  }

  /**
   * Shows validation error message.
   */
  #showValidationError() {
    const klaviyoErrorMessage = /** @type {HTMLElement | null} */ (
      this.form?.querySelector(`#Email-signup__message-klaviyo-error-${this.blockId}`)
    );
    const klaviyoSuccessMessage = /** @type {HTMLElement | null} */ (
      this.form?.querySelector(`#Email-signup__message-klaviyo-success-${this.blockId}`)
    );
    const shopifySuccessMessage = this.form?.querySelector(
      `#Email-signup__message-success-${this.blockId}`
    );

    if (klaviyoSuccessMessage) {
      klaviyoSuccessMessage.hidden = true;
    }

    if (klaviyoErrorMessage) {
      const messageText = klaviyoErrorMessage.querySelector('.email-signup__message-text');
      if (messageText) {
        const validationText = messageText.getAttribute('data-validation-text');
        if (validationText) {
          messageText.textContent = validationText;
        }
      }
      klaviyoErrorMessage.hidden = false;
      klaviyoErrorMessage.focus();
      this.emailInput?.setAttribute('aria-invalid', 'true');
      this.emailInput?.setAttribute('aria-describedby', klaviyoErrorMessage.id);
    }

    if (shopifySuccessMessage) {
      shopifySuccessMessage.classList.add('email-signup__message--hidden');
    }
  }

  /**
   * Shows error message.
   */
  #showError() {
    const errorMessage = /** @type {HTMLElement | null} */ (
      this.form?.querySelector(`#Email-signup__message-klaviyo-error-${this.blockId}`)
    );
    const klaviyoSuccessMessage = /** @type {HTMLElement | null} */ (
      this.form?.querySelector(`#Email-signup__message-klaviyo-success-${this.blockId}`)
    );
    const shopifySuccessMessage = this.form?.querySelector(
      `#Email-signup__message-success-${this.blockId}`
    );

    if (klaviyoSuccessMessage) {
      klaviyoSuccessMessage.hidden = true;
    }

    if (errorMessage) {
      const messageText = errorMessage.querySelector('.email-signup__message-text');
      if (messageText) {
        const defaultText = messageText.getAttribute('data-default-text');
        if (defaultText) {
          messageText.textContent = defaultText;
        }
      }
      errorMessage.hidden = false;
      errorMessage.focus();
      this.emailInput?.setAttribute('aria-describedby', errorMessage.id);
    }

    if (shopifySuccessMessage) {
      shopifySuccessMessage.classList.add('email-signup__message--hidden');
    }
  }

  /**
   * Hides all messages.
   */
  #hideMessages() {
    const klaviyoSuccessMessage = /** @type {HTMLElement | null} */ (
      this.form?.querySelector(`#Email-signup__message-klaviyo-success-${this.blockId}`)
    );
    const klaviyoErrorMessage = /** @type {HTMLElement | null} */ (
      this.form?.querySelector(`#Email-signup__message-klaviyo-error-${this.blockId}`)
    );
    const shopifySuccessMessage = this.form?.querySelector(
      `#Email-signup__message-success-${this.blockId}`
    );
    const shopifyErrorMessage = /** @type {HTMLElement | null} */ (
      this.form?.querySelector(`#Email-signup__message-error-${this.blockId}`)
    );

    if (klaviyoSuccessMessage) klaviyoSuccessMessage.hidden = true;
    if (klaviyoErrorMessage) klaviyoErrorMessage.hidden = true;
    if (shopifySuccessMessage) {
      shopifySuccessMessage.classList.add('email-signup__message--hidden');
    }
    if (shopifyErrorMessage) shopifyErrorMessage.hidden = true;

    this.emailInput?.removeAttribute('aria-invalid');
    this.emailInput?.removeAttribute('aria-describedby');
  }

  /**
   * Sets button loading state.
   * @param {boolean} loading - Whether the button should be in loading state.
   */
  #setButtonLoading(loading) {
    if (this.submitButton) {
      this.submitButton.disabled = loading;
      this.submitButton.setAttribute('aria-busy', loading ? 'true' : 'false');
    }
  }
}

if (!customElements.get('email-signup-form')) {
  customElements.define('email-signup-form', EmailSignupFormComponent);
}
